import React, { useEffect, useMemo, useState } from "react";
import { AlertDialog, Box, Button, Flex, Select, Switch, Text, TextArea, TextField } from "@radix-ui/themes";
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard, PermissionAlertDialog, ToastAlert } from "components";
import {
  useDeactivateWhatsappCredentials,
  useDeleteWhatsappCredentialAccount,
  useGetWhatsappCredentialAccounts,
  useSaveWhatsappCredentialAccounts,
  useValidateWhatsappCredentialAccount,
} from "hooks";
import { useToast } from "../../../context/ToastContext";
import { useProfileContext } from "context";
import { AuthWhatsappDialog } from "./AuthWhatsappDialog";
import { DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE, normalizeWhatsappCredentialAccount, serializeWhatsappCredentialAccount } from "./whatsappCredentialsStore";

function buildDefaultFormData() {
  return {
    accountName: "",
    appId: "",
    businessId: "",
    accessToken: "",
    autoReplyEnabled: true,
    autoReplyMessage: DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE,
  };
}

function toStringValue(value) {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value).trim();
}

function getApiErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.message || error?.response?.data?.error || fallbackMessage;
}

function mapAccountToForm(account) {
  return {
    accountName: account?.accountName || "",
    appId: account?.appId || "",
    businessId: account?.businessId || "",
    accessToken: "",
    autoReplyEnabled: account?.autoReplyEnabled ?? true,
    autoReplyMessage: account?.autoReplyMessage || DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE,
  };
}

function whatsappAccountFormHasChanges(formData, account) {
  if (!account) {
    return false;
  }

  return (
    toStringValue(formData.accountName) !== toStringValue(account.accountName)
    || toStringValue(formData.appId) !== toStringValue(account.appId)
    || toStringValue(formData.businessId) !== toStringValue(account.businessId)
    || Boolean(toStringValue(formData.accessToken))
    || Boolean(formData.autoReplyEnabled) !== Boolean(account.autoReplyEnabled ?? true)
    || toStringValue(formData.autoReplyMessage || DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE) !== toStringValue(account.autoReplyMessage || DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE)
  );
}

function CredentialInput({ label, value, placeholder, type = "text", invalid, errorMessage, disabled, onChange }) {
  return (
    <Flex direction="column" gap="1">
      <Text size="2" weight="medium">
        {label}
      </Text>
      <TextField.Root
        type={type}
        value={value}
        placeholder={placeholder}
        color={invalid ? "red" : undefined}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        style={{ width: "100%" }}
      />
      {invalid && errorMessage ? (
        <Text size="1" color="red">
          {errorMessage}
        </Text>
      ) : null}
    </Flex>
  );
}

function mergeSavedAccounts(currentAccounts, savedAccounts) {
  const savedKeys = new Set(savedAccounts.map((account) => `${account.appId}:${account.businessId}`));
  return [
    ...currentAccounts.filter((account) => !savedKeys.has(`${account.appId}:${account.businessId}`)),
    ...savedAccounts,
  ];
}

export function ActiveWhatsappIntegration() {
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const isOwner = userProfile?.user_role === "Owner";
  const { data: accountsData } = useGetWhatsappCredentialAccounts(userProfile?.organisation_id);
  const saveWhatsappCredentialAccounts = useSaveWhatsappCredentialAccounts();
  const deleteWhatsappCredentialAccount = useDeleteWhatsappCredentialAccount();
  const validateWhatsappCredentialAccount = useValidateWhatsappCredentialAccount();
  const deactivateWhatsappCredentials = useDeactivateWhatsappCredentials();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [formData, setFormData] = useState(buildDefaultFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [openAddAccountDialog, setOpenAddAccountDialog] = useState(false);
  const [addAccountFormData, setAddAccountFormData] = useState(buildDefaultFormData);
  const [addAccountFieldErrors, setAddAccountFieldErrors] = useState({});
  const [pendingAccounts, setPendingAccounts] = useState([]);

  useEffect(() => {
    const nextAccounts = (accountsData?.data?.accounts || []).map(normalizeWhatsappCredentialAccount);
    setAccounts(nextAccounts);
    setSelectedAccountId((currentId) => {
      if (nextAccounts.some((account) => account.id === currentId)) {
        return currentId;
      }

      return nextAccounts[0]?.id || "";
    });
  }, [accountsData]);

  const selectedAccount = useMemo(() => {
    return accounts.find((account) => account.id === selectedAccountId) || accounts[0] || null;
  }, [accounts, selectedAccountId]);

  const hasSelectedAccountChanges = useMemo(() => {
    return whatsappAccountFormHasChanges(formData, selectedAccount);
  }, [formData, selectedAccount]);

  useEffect(() => {
    setFormData(mapAccountToForm(selectedAccount));
    setFieldErrors({});
  }, [selectedAccount]);

  const requireOwner = (callback) => {
    if (isOwner) {
      callback();
      return;
    }

    setShowPermissionAlert(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((previousData) => ({
      ...previousData,
      [fieldName]: value,
    }));
    setFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
  };

  const handleAddAccountFieldChange = (fieldName, value) => {
    setAddAccountFormData((previousData) => ({
      ...previousData,
      [fieldName]: value,
    }));
    setAddAccountFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
  };

  const validateAccountForm = (data, requireAccessToken = true) => {
    const nextErrors = {
      accountName: !toStringValue(data.accountName),
      appId: !toStringValue(data.appId),
      businessId: !toStringValue(data.businessId),
      accessToken: requireAccessToken && !toStringValue(data.accessToken),
    };

    return nextErrors;
  };

  const handleSaveSelectedAccount = () => {
    requireOwner(async () => {
      if (!selectedAccount) {
        return;
      }

      const credentialsKeyChanged = (
        toStringValue(formData.appId) !== toStringValue(selectedAccount.appId)
        || toStringValue(formData.businessId) !== toStringValue(selectedAccount.businessId)
      );
      const nextErrors = validateAccountForm(formData, credentialsKeyChanged);
      setFieldErrors(nextErrors);
      if (Object.values(nextErrors).some(Boolean)) {
        return;
      }

      const updatedAccount = normalizeWhatsappCredentialAccount({
        ...selectedAccount,
        accountName: formData.accountName,
        appId: formData.appId,
        businessId: formData.businessId,
        accessToken: formData.accessToken,
        autoReplyEnabled: formData.autoReplyEnabled,
        autoReplyMessage: formData.autoReplyMessage,
      });
      try {
        const result = await saveWhatsappCredentialAccounts.mutateAsync({
          organisation_id: userProfile.organisation_id,
          accounts: [serializeWhatsappCredentialAccount(updatedAccount)],
        });
        const savedAccounts = (result.data.accounts || []).map(normalizeWhatsappCredentialAccount);
        setAccounts(mergeSavedAccounts(accounts, savedAccounts));
        setSelectedAccountId(savedAccounts.find((account) => (
          account.appId === updatedAccount.appId && account.businessId === updatedAccount.businessId
        ))?.id || updatedAccount.id || "");
        setToastMessage("WhatsApp credentials saved");
      } catch (error) {
        setToastMessage(getApiErrorMessage(error, "Unable to save WhatsApp credentials right now"));
      }
    });
  };

  const handleStageAddAccount = async () => {
    const nextErrors = validateAccountForm(addAccountFormData, true);
    setAddAccountFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }
    if (accounts.some((account) => (
      account.appId === toStringValue(addAccountFormData.appId)
      && account.businessId === toStringValue(addAccountFormData.businessId)
    ))) {
      setToastMessage("That WhatsApp app and business account combination is already connected");
      return;
    }

    try {
      await validateWhatsappCredentialAccount.mutateAsync({
        organisation_id: userProfile.organisation_id,
        account_name: addAccountFormData.accountName,
        app_id: addAccountFormData.appId,
        business_id: addAccountFormData.businessId,
        access_token: addAccountFormData.accessToken,
        auto_reply_enabled: addAccountFormData.autoReplyEnabled,
        auto_reply_message: addAccountFormData.autoReplyMessage,
      });
      const nextAccount = normalizeWhatsappCredentialAccount(addAccountFormData, accounts.length + pendingAccounts.length);
      setPendingAccounts((previousAccounts) => {
        const remainingAccounts = previousAccounts.filter((account) => (
          account.appId !== nextAccount.appId || account.businessId !== nextAccount.businessId
        ));
        return [...remainingAccounts, nextAccount];
      });
      setAddAccountFormData(buildDefaultFormData());
      setAddAccountFieldErrors({});
      setToastMessage("WhatsApp account validated");
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to validate WhatsApp credentials"));
    }
  };

  const handleRemovePendingAccount = (accountClientKey) => {
    setPendingAccounts((previousAccounts) => previousAccounts.filter((account) => account.clientKey !== accountClientKey));
  };

  const handleAddAccounts = async () => {
    if (pendingAccounts.length === 0) {
      return;
    }

    try {
      const result = await saveWhatsappCredentialAccounts.mutateAsync({
        organisation_id: userProfile.organisation_id,
        accounts: [...accounts, ...pendingAccounts].map(serializeWhatsappCredentialAccount),
      });
      const savedAccounts = (result.data.accounts || []).map(normalizeWhatsappCredentialAccount);
      const nextAccounts = mergeSavedAccounts(accounts, savedAccounts);
      setAccounts(nextAccounts);
      const lastPendingAccount = pendingAccounts[pendingAccounts.length - 1];
      setSelectedAccountId(savedAccounts.find((account) => (
        account.appId === lastPendingAccount.appId && account.businessId === lastPendingAccount.businessId
      ))?.id || "");
      setToastMessage("WhatsApp account added");
      setOpenAddAccountDialog(false);
      setPendingAccounts([]);
      setAddAccountFormData(buildDefaultFormData());
      setAddAccountFieldErrors({});
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to add WhatsApp account right now"));
    }
  };

  const handleRemoveSelectedAccount = async () => {
    if (!selectedAccount) {
      return;
    }

    try {
      await deleteWhatsappCredentialAccount.mutateAsync({
        organisation_id: userProfile.organisation_id,
        account_id: selectedAccount.id,
      });
      setShowRemoveDialog(false);
      setToastMessage("WhatsApp account removed");
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to remove WhatsApp account right now"));
    }
  };

  const handleDisconnect = async () => {
    try {
      await deactivateWhatsappCredentials.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });
      setShowDisconnectDialog(false);
      setToastMessage("WhatsApp integration disconnected");
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to disconnect WhatsApp right now"));
    }
  };

  return (
    <>
      <ActiveIntegrateCard.Root
        name="WhatsApp"
        subtitle="CRM & Marketing messages"
        image_url="whatsapp.png"
        showLocationBadge={false}
        option={{
          text: "Disconnect",
          color: "red",
          onClick: () => requireOwner(() => setShowDisconnectDialog(true)),
        }}
      >
        <ActiveIntegrateCard.Section
          name="Credentials"
          subtitle="Manage WhatsApp Business credentials used for template review and messaging."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex justify="between" align="center">
              <Flex direction="column" gap="1" align="start">
                <Text size="2" weight="medium">
                  Connected accounts
                </Text>
                <Text size="1" color="gray">
                  {accounts.length} account{accounts.length === 1 ? "" : "s"} connected
                </Text>
              </Flex>
              <Button size="2" variant="soft" disabled={!isOwner} onClick={() => requireOwner(() => setOpenAddAccountDialog(true))}>
                <PlusIcon width="14" height="14" />
                Add account
              </Button>
            </Flex>

            {accounts.length > 1 ? (
              <Flex direction="column" gap="1" align="start">
                <Text size="2" weight="medium">
                  Selected account
                </Text>
                <Select.Root value={selectedAccountId || ""} onValueChange={setSelectedAccountId}>
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {accounts.map((account) => (
                      <Select.Item key={account.id} value={account.id}>
                        {account.accountName || `WhatsApp Business Account ${account.businessId}`}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            ) : null}

            {selectedAccount ? (
              <Flex direction="column" gap="3" style={{ width: "100%" }}>
                <Text size="1" color="gray">
                  App {selectedAccount.appId} · WhatsApp Business Account {selectedAccount.businessId} · {selectedAccount.maskedAccessToken}
                </Text>
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "var(--space-3)",
                  }}
                >
                  <CredentialInput
                    label="Account name"
                    placeholder="Main WhatsApp account"
                    value={formData.accountName}
                    invalid={fieldErrors.accountName}
                    errorMessage="Account name is required."
                    disabled={!isOwner}
                    onChange={(value) => handleFieldChange("accountName", value)}
                  />
                  <CredentialInput
                    label="App ID"
                    placeholder="123456789012345"
                    value={formData.appId}
                    invalid={fieldErrors.appId}
                    errorMessage="App ID is required."
                    disabled={!isOwner}
                    onChange={(value) => handleFieldChange("appId", value)}
                  />
                  <CredentialInput
                    label="WhatsApp Business Account ID"
                    placeholder="987654321098765"
                    value={formData.businessId}
                    invalid={fieldErrors.businessId}
                    errorMessage="WhatsApp Business Account ID is required."
                    disabled={!isOwner}
                    onChange={(value) => handleFieldChange("businessId", value)}
                  />
                  <CredentialInput
                    label="Access token"
                    type="password"
                    placeholder={selectedAccount.maskedAccessToken}
                    value={formData.accessToken}
                    invalid={fieldErrors.accessToken}
                    errorMessage="Access token is required when changing credentials."
                    disabled={!isOwner}
                    onChange={(value) => handleFieldChange("accessToken", value)}
                  />
                </Box>

                <Flex
                  direction="column"
                  gap="3"
                  p="3"
                  style={{
                    border: "1px solid var(--gray-a6)",
                    borderRadius: "var(--radius-3)",
                    background: "var(--gray-1)",
                  }}
                >
                  <Flex justify="between" align="center" gap="3">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">
                        Automated inbound reply
                      </Text>
                      <Text size="1" color="gray">
                        Sent when a guest replies to one of your WhatsApp messages.
                      </Text>
                    </Flex>
                    <Switch
                      checked={Boolean(formData.autoReplyEnabled)}
                      disabled={!isOwner}
                      onCheckedChange={(checked) => handleFieldChange("autoReplyEnabled", checked)}
                    />
                  </Flex>
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">
                      Reply message
                    </Text>
                    <TextArea
                      value={formData.autoReplyMessage}
                      disabled={!isOwner || !formData.autoReplyEnabled}
                      onChange={(event) => handleFieldChange("autoReplyMessage", event.target.value)}
                      style={{ minHeight: 96 }}
                    />
                  </Flex>
                </Flex>

                <Flex justify="end" align="center" gap="3">
                  <Button
                    variant="soft"
                    color="red"
                    onClick={() => requireOwner(() => setShowRemoveDialog(true))}
                    disabled={!isOwner || accounts.length <= 1 || deleteWhatsappCredentialAccount.isPending}
                  >
                    <TrashIcon width="14" height="14" />
                    Remove account
                  </Button>
                  <Button
                    variant="soft"
                    color="accent"
                    onClick={handleSaveSelectedAccount}
                    disabled={!isOwner || !hasSelectedAccountChanges || saveWhatsappCredentialAccounts.isPending}
                  >
                    {saveWhatsappCredentialAccounts.isPending ? "Saving..." : "Save changes"}
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Flex justify="center" py="4">
                <Text size="2" color="gray">
                  No connected accounts
                </Text>
              </Flex>
            )}
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <AuthWhatsappDialog
        isOpen={openAddAccountDialog}
        onOpenChange={(isOpen) => {
          setOpenAddAccountDialog(isOpen);
          if (!isOpen) {
            setPendingAccounts([]);
            setAddAccountFormData(buildDefaultFormData());
            setAddAccountFieldErrors({});
          }
        }}
        onCancel={() => {
          setPendingAccounts([]);
          setAddAccountFormData(buildDefaultFormData());
          setAddAccountFieldErrors({});
        }}
        onContinue={handleAddAccounts}
        onAddAccount={handleStageAddAccount}
        onRemoveAccount={handleRemovePendingAccount}
        accounts={pendingAccounts}
        formData={addAccountFormData}
        onFieldChange={handleAddAccountFieldChange}
        fieldErrors={addAccountFieldErrors}
        isAddAccountLoading={validateWhatsappCredentialAccount.isPending}
        isContinueLoading={saveWhatsappCredentialAccounts.isPending}
        isContinueDisabled={pendingAccounts.length === 0}
      />

      <AlertDialog.Root open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Disconnect WhatsApp?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes all saved WhatsApp credentials from this organisation.
          </AlertDialog.Description>
          <Flex gap="3" justify="end" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleDisconnect} disabled={deactivateWhatsappCredentials.isPending}>
                Disconnect
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Remove WhatsApp account?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes the selected WhatsApp credential account.
          </AlertDialog.Description>
          <Flex gap="3" justify="end" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleRemoveSelectedAccount} disabled={deleteWhatsappCredentialAccount.isPending}>
                Remove account
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
      <ToastAlert />
    </>
  );
}
